/**
 * One region list for US states (including the territories the ship-to parser accepts),
 * Canadian provinces, and Mexican states. Each consumer keeps its own country subset.
 */

export type RegionCountry = "US" | "CA" | "MX";

export interface RegionCode {
  country: RegionCountry;
  code: string;
  /** Lowercase lookup name. */
  name: string;
}

const region = (country: RegionCountry, code: string, name: string): RegionCode => ({ country, code, name });

const US_REGIONS: readonly RegionCode[] = [
  region("US", "AL", "alabama"),
  region("US", "AK", "alaska"),
  region("US", "AZ", "arizona"),
  region("US", "AR", "arkansas"),
  region("US", "CA", "california"),
  region("US", "CO", "colorado"),
  region("US", "CT", "connecticut"),
  region("US", "DE", "delaware"),
  region("US", "FL", "florida"),
  region("US", "GA", "georgia"),
  region("US", "HI", "hawaii"),
  region("US", "ID", "idaho"),
  region("US", "IL", "illinois"),
  region("US", "IN", "indiana"),
  region("US", "IA", "iowa"),
  region("US", "KS", "kansas"),
  region("US", "KY", "kentucky"),
  region("US", "LA", "louisiana"),
  region("US", "ME", "maine"),
  region("US", "MD", "maryland"),
  region("US", "MA", "massachusetts"),
  region("US", "MI", "michigan"),
  region("US", "MN", "minnesota"),
  region("US", "MS", "mississippi"),
  region("US", "MO", "missouri"),
  region("US", "MT", "montana"),
  region("US", "NE", "nebraska"),
  region("US", "NV", "nevada"),
  region("US", "NH", "new hampshire"),
  region("US", "NJ", "new jersey"),
  region("US", "NM", "new mexico"),
  region("US", "NY", "new york"),
  region("US", "NC", "north carolina"),
  region("US", "ND", "north dakota"),
  region("US", "OH", "ohio"),
  region("US", "OK", "oklahoma"),
  region("US", "OR", "oregon"),
  region("US", "PA", "pennsylvania"),
  region("US", "RI", "rhode island"),
  region("US", "SC", "south carolina"),
  region("US", "SD", "south dakota"),
  region("US", "TN", "tennessee"),
  region("US", "TX", "texas"),
  region("US", "UT", "utah"),
  region("US", "VT", "vermont"),
  region("US", "VA", "virginia"),
  region("US", "WA", "washington"),
  region("US", "WV", "west virginia"),
  region("US", "WI", "wisconsin"),
  region("US", "WY", "wyoming"),
  region("US", "DC", "district of columbia"),
  region("US", "AS", "american samoa"),
  region("US", "GU", "guam"),
  region("US", "MP", "northern mariana islands"),
  region("US", "PR", "puerto rico"),
  region("US", "VI", "virgin islands"),
  region("US", "VI", "u.s. virgin islands"),
];

const CA_REGIONS: readonly RegionCode[] = [
  region("CA", "AB", "alberta"),
  region("CA", "BC", "british columbia"),
  region("CA", "MB", "manitoba"),
  region("CA", "NB", "new brunswick"),
  region("CA", "NL", "newfoundland and labrador"),
  region("CA", "NL", "newfoundland"),
  region("CA", "NT", "northwest territories"),
  region("CA", "NS", "nova scotia"),
  region("CA", "NU", "nunavut"),
  region("CA", "ON", "ontario"),
  region("CA", "PE", "prince edward island"),
  region("CA", "QC", "quebec"),
  region("CA", "QC", "québec"),
  region("CA", "SK", "saskatchewan"),
  region("CA", "YT", "yukon"),
  region("CA", "YT", "yukon territory"),
];

const MX_REGIONS: readonly RegionCode[] = [
  region("MX", "AG", "aguascalientes"),
  region("MX", "BC", "baja california"),
  region("MX", "BS", "baja california sur"),
  region("MX", "CM", "campeche"),
  region("MX", "CS", "chiapas"),
  region("MX", "CH", "chihuahua"),
  region("MX", "CO", "coahuila"),
  region("MX", "CL", "colima"),
  region("MX", "DF", "ciudad de mexico"),
  region("MX", "DF", "ciudad de méxico"),
  region("MX", "DF", "distrito federal"),
  region("MX", "DG", "durango"),
  region("MX", "GT", "guanajuato"),
  region("MX", "GR", "guerrero"),
  region("MX", "HG", "hidalgo"),
  region("MX", "JA", "jalisco"),
  region("MX", "EM", "estado de mexico"),
  region("MX", "EM", "estado de méxico"),
  region("MX", "MI", "michoacan"),
  region("MX", "MI", "michoacán"),
  region("MX", "MO", "morelos"),
  region("MX", "NA", "nayarit"),
  region("MX", "NL", "nuevo leon"),
  region("MX", "NL", "nuevo león"),
  region("MX", "OA", "oaxaca"),
  region("MX", "PU", "puebla"),
  region("MX", "QT", "queretaro"),
  region("MX", "QT", "querétaro"),
  region("MX", "QR", "quintana roo"),
  region("MX", "SL", "san luis potosi"),
  region("MX", "SL", "san luis potosí"),
  region("MX", "SI", "sinaloa"),
  region("MX", "SO", "sonora"),
  region("MX", "TB", "tabasco"),
  region("MX", "TM", "tamaulipas"),
  region("MX", "TL", "tlaxcala"),
  region("MX", "VE", "veracruz"),
  region("MX", "YU", "yucatan"),
  region("MX", "YU", "yucatán"),
  region("MX", "ZA", "zacatecas"),
];

export const REGION_CODES: readonly RegionCode[] = [...US_REGIONS, ...CA_REGIONS, ...MX_REGIONS];

/** Name to code for US only. QBO cutover keeps this country subset. */
export const US_STATE_ABBREVIATIONS: Record<string, string> = Object.fromEntries(
  US_REGIONS.filter(entry => entry.name !== "u.s. virgin islands").map(entry => [entry.name, entry.code]),
);

/** Union of the three former US lists, including DC and the territories the ship-to parser accepts. */
export const US_STATE_CODES: ReadonlySet<string> = new Set(US_REGIONS.map(entry => entry.code));

const byName = new Map<string, RegionCode>();
for (const entry of REGION_CODES) {
  if (!byName.has(entry.name)) {
    byName.set(entry.name, entry);
  }
}

export function regionCodesFor(country: RegionCountry): string[] {
  const seen = new Set<string>();
  const codes: string[] = [];
  for (const entry of REGION_CODES) {
    if (entry.country !== country || seen.has(entry.code)) {
      continue;
    }
    seen.add(entry.code);
    codes.push(entry.code);
  }
  return codes;
}

export function isUsStateCode(value: string): boolean {
  return US_STATE_CODES.has(value.trim().toUpperCase());
}

export function regionByName(value: string): RegionCode | null {
  const key = value.trim().toLowerCase().replace(/\./g, "").replace(/\s+/g, " ");
  return byName.get(key) ?? null;
}

/** Codes that exist in more than one of US, CA, and MX cannot fill a blank country. */
export function regionsForCode(code: string, country?: RegionCountry | null): RegionCode[] {
  const normalized = code.trim().toUpperCase();
  const seen = new Set<string>();
  const matches: RegionCode[] = [];
  for (const entry of REGION_CODES) {
    if (entry.code !== normalized) {
      continue;
    }
    if (country && entry.country !== country) {
      continue;
    }
    const key = `${entry.country}:${entry.code}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    matches.push(entry);
  }
  return matches;
}
